import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  updateAccommodationUnit,
  getAccommodationUnitById,
  submitPhotosToAccommodationUnit,
} from "../../services/accommodationUnits";
import { getBookedDatesByBookableId } from "../../services/bookings";
import {
  deleteUnavailableDateRangesForBookable,
  getUnavailableDateRangesForBookable,
  bulkCreateUnavailableDateRangesForBookable
} from "../../services/unavailable_dates"
import AccommodationUnitForm from "../../components/accommodation-unit-form/AccommodationUnitForm";
import { getApiErrorMessage, preparePhotosForUpload } from "../../utils/helpers";
import { useEntityUpdate, usePhotoUpload } from "../../utils/sharedMutations";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ACCOMMODATION_TYPES } from "../../constants";
import { BOOKING_TYPES } from "../../constants";

/** Same range can appear twice in UI state; DB should receive each range once. */
function dedupeDateRanges(ranges) {
  if (!Array.isArray(ranges)) return [];
  const seen = new Set();
  return ranges.filter(([start, end]) => {
    const key = `${start}|${end}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function EditAccommodationUnit() {
  const { accommodationUnitId } = useParams();
  const toast = useToast();
  const { t } = useTranslation("common");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["accommodation-units", accommodationUnitId],
    queryFn: () => getAccommodationUnitById(accommodationUnitId),
    select: (data) => data.data,
    onSuccess: (unitData) => {
      if (unitData?.accommodationType === ACCOMMODATION_TYPES.HOTEL) return;
      refetchUnavailableDateRanges();
      refetchBookedDates();
    }
  });

  const isHotelAccommodation =
    data?.accommodationType === ACCOMMODATION_TYPES.HOTEL;

  const { data: dateRanges, isLoading: isLoadingDateRanges, refetch: refetchUnavailableDateRanges } = useQuery({
    queryKey: ["dateRanges", data],
    queryFn: () => getUnavailableDateRangesForBookable(data.id, BOOKING_TYPES.STAY),
    enabled: !!data && !isHotelAccommodation,
    select: (data) => data.data.results,
    onSuccess: (data) => {
      //console.log(data)
    },
  })

  const { data: bookedDates, isLoading: isLoadingBookedDates, refetch: refetchBookedDates } = useQuery({
    queryKey: ["bookedDates", data],
    queryFn: () => getBookedDatesByBookableId(data.id, BOOKING_TYPES.STAY),
    enabled: !!data && !isHotelAccommodation,
    select: (data) => data.data.results,
    onSuccess: (data) => {
    },
  })

  const submitPhotosMutation = usePhotoUpload(
    ({ accommodationUnitId, photos }) =>
      submitPhotosToAccommodationUnit(accommodationUnitId, photos)
  );

  const editAccommodationUnitMutation = useEntityUpdate(
    (accommodationUnitData) =>
      updateAccommodationUnit({
        ...accommodationUnitData,
        id: data.id,
      })
  );

  const onSubmit = async (
    //accommodationId = null,
    accommodationUnitData,
    photos,
    unavailableDateRanges
  ) => {
    await editAccommodationUnitMutation.mutateAsync(
      accommodationUnitData
    );
    if (photos?.length > 0) {
      await submitPhotosMutation.mutateAsync({
        //accommodationUnitId: accommodationUnit.data.id,
        accommodationUnitId: accommodationUnitData.id,
        photos: preparePhotosForUpload(photos),
      });
    }
    if (isHotelAccommodation) {
      refetch();
      return;
    }
    try {
      await deleteUnavailableDateRangesForBookable(
        accommodationUnitData.id,
        BOOKING_TYPES.STAY
      );
      const uniqueRanges = dedupeDateRanges(unavailableDateRanges);
      await bulkCreateUnavailableDateRangesForBookable(
        accommodationUnitData.id,
        BOOKING_TYPES.STAY,
        uniqueRanges
      );
    } catch (error) {
      toast({
        title: t("unavailableDatesSaveError"),
        description: getApiErrorMessage(error, t("unavailableDatesSaveError")),
        status: "error",
        duration: 7000,
        isClosable: true,
        position: "top",
      });
      refetch();
      return;
    }
    refetch();
  };

  if (
    isLoading ||
    (!isHotelAccommodation &&
      data &&
      (isLoadingDateRanges || isLoadingBookedDates))
  ) {
    return <div>Loading...</div>;
  }

  return (
    <AccommodationUnitForm
      editable
      accommodationUnitData={{
        ...data,
        seasonalPricesAttributes: data.seasonalPrices,
      }}
      onSubmit={onSubmit}
      isSubmitting={
        editAccommodationUnitMutation.isLoading ||
        submitPhotosMutation.isLoading
      }
      allowPhotoUpload={isHotelAccommodation}
      showUnavailableDatesCalendar={!isHotelAccommodation}
      bookedDates={isHotelAccommodation ? [] : bookedDates}
      dateRangesFromDatabase={isHotelAccommodation ? [] : dateRanges}
    />
  );
}

export default EditAccommodationUnit;
